import type { BuckerarrestorPassPreviewRow } from './buckerarrestorRowsToTlogExcelRows';

export interface BuckerarrestorPreviewMetricApiRow {
  pass_name: string;
  weld_uid: string;
  zone_name: string;
  volts_low: number | null;
  volts_avg: number | null;
  volts_high: number | null;
  current_low: number | null;
  current_avg: number | null;
  current_high: number | null;
  travel_speed_low: number | null;
  travel_speed_avg: number | null;
  travel_speed_high: number | null;
  heat_avg: number | null;
  heat_low: number | null;
  heat_high: number | null;
  /** Arc duration (s) from txt — server maps to pass/weld weld_time (minutes). */
  duration_seconds: number | null;
  /** Pass start unix seconds — pass/zone/tilt s_time. */
  s_time: number | null;
  /** Pass end unix seconds — pass c_time / zone·tilt s_c_time. */
  c_time: number | null;
}

export interface BuckerarrestorUpsertPreviewMetricsPayload {
  project_id: number;
  machine_type: string;
  weld_number: string;
  job_number: string;
  skip_status_calculation: true;
  /** Marks weld_status.is_fileUpload on the server (Manual TXT upload). */
  is_fileUpload: true;
  pass_metrics: BuckerarrestorPreviewMetricApiRow[];
  zone_metrics: BuckerarrestorPreviewMetricApiRow[];
  tilt_metrics: BuckerarrestorPreviewMetricApiRow[];
}

const toNumOrNull = (value: string | undefined): number | null => {
  if (value == null || String(value).trim() === '') return null;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
};

/** Map one preview table row → API metric row (same numbers as UI). */
export function previewRowToApiMetric(row: BuckerarrestorPassPreviewRow): BuckerarrestorPreviewMetricApiRow {
  const heat = toNumOrNull(row.heatInput);
  return {
    pass_name: row.passName,
    weld_uid: row.weldUid,
    zone_name: row.passName,
    volts_low: toNumOrNull(row.voltsMin),
    volts_avg: toNumOrNull(row.voltsAvg),
    volts_high: toNumOrNull(row.voltsMax),
    current_low: toNumOrNull(row.currentMin),
    current_avg: toNumOrNull(row.currentAvg),
    current_high: toNumOrNull(row.currentMax),
    travel_speed_low: toNumOrNull(row.travelMin),
    travel_speed_avg: toNumOrNull(row.travelAvg),
    travel_speed_high: toNumOrNull(row.travelMax),
    heat_avg: heat,
    heat_low: heat,
    heat_high: heat,
    duration_seconds:
      row.durationSeconds != null && Number.isFinite(row.durationSeconds)
        ? row.durationSeconds
        : null,
    s_time:
      row.startUnixSeconds != null && Number.isFinite(row.startUnixSeconds)
        ? row.startUnixSeconds
        : null,
    c_time:
      row.endUnixSeconds != null && Number.isFinite(row.endUnixSeconds)
        ? row.endUnixSeconds
        : null,
  };
}

/**
 * Build POST /view/upsert-preview-metrics body from Buckerarrestor preview rows.
 * Zone/tilt mirror pass metrics (Buckerarrestor SUMMARY is pass-level).
 */
export function buildBuckerarrestorUpsertPreviewMetricsPayload(
  previewRows: BuckerarrestorPassPreviewRow[],
  options: {
    projectId: number;
    machineType: string;
    weldId: string;
    jobNumber?: string;
  }
): BuckerarrestorUpsertPreviewMetricsPayload {
  const pass_metrics = previewRows.map(previewRowToApiMetric);
  return {
    project_id: options.projectId,
    machine_type: options.machineType,
    weld_number: String(options.weldId || '').trim(),
    job_number: String(options.jobNumber || 'JOB-1').trim() || 'JOB-1',
    skip_status_calculation: true,
    is_fileUpload: true,
    pass_metrics,
    zone_metrics: pass_metrics,
    tilt_metrics: pass_metrics,
  };
}
